import { RadioGroup } from "@ark-ui/solid";
import type { VoidComponent } from "solid-js";
import { For, untrack } from "solid-js";
import type { ExtendableInputProps } from "../inputs/Input";
import Input from "../inputs/Input";
import { useTranslation } from "../../state/i18n";
import { type HexColor, type ThemeId, THEMES } from "../../types";
import Radio from "../radio/Radio";

type ThemeSelectProps = {
  defaultValue: ThemeId;
  onChange(value: ThemeId): void;
} & ExtendableInputProps;

const ThemeSelect: VoidComponent<ThemeSelectProps> = (props) => {
  const { t } = useTranslation();

  return (
    <RadioGroup.Root
      defaultValue={untrack(() => props.defaultValue)}
      onValueChange={(details) => {
        props.onChange(details.value as ThemeId);
      }}
      aria-label={props.label}
    >
      <Input label={props.label} required={props.required} info={props.info}>
        {() => (
          <div class="grid grid-cols-4 gap-2">
            <For
              each={[
                THEMES.Default,
                THEMES.Green,
                THEMES.Purple,
                THEMES.Blue,
                THEMES.Brown,
                THEMES.Gray,
                THEMES.Pink,
                THEMES.Red,
              ]}
            >
              {(theme) => (
                <RadioGroup.Item
                  value={theme.id}
                  class="group relative flex h-41 flex-col overflow-hidden rounded-sm border border-subtle focussed:outline-default"
                >
                  <div class="flex h-32 items-center justify-center border-b border-subtle bg-surface-extra-subtle group-checked:bg-surface-active-brand group-hover:bg-surface-hover">
                    {browserImage(theme.color)}
                  </div>
                  <div class="flex grow items-center gap-1.5 bg-surface-default px-3">
                    <RadioGroup.ItemControl>
                      <Radio />
                    </RadioGroup.ItemControl>
                    <RadioGroup.ItemText class="text-base-regular text-secondary">
                      {t(`FORM_SETTINGS_THEME_NAME_${theme.id}` as unknown as Parameters<typeof t>[0])}
                    </RadioGroup.ItemText>
                    <RadioGroup.ItemHiddenInput />
                  </div>
                </RadioGroup.Item>
              )}
            </For>
          </div>
        )}
      </Input>
    </RadioGroup.Root>
  );
};

const browserImage = (themeColor: HexColor) => (
  <svg width="114" height="80" viewBox="0 0 114 80" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M113 7.5H113.5V7V3C113.5 1.61929 112.381 0.5 111 0.5H3C1.61929 0.5 0.5 1.61929 0.5 3V7V7.5H1H113Z"
      fill="#F9FAFB"
    />
    <path
      d="M113 7.5H113.5V7V3C113.5 1.61929 112.381 0.5 111 0.5H3C1.61929 0.5 0.5 1.61929 0.5 3V7V7.5H1H113Z"
      stroke="#E5E7EB"
    />
    <circle cx="4" cy="4" r="1" fill="#DC2626" />
    <circle cx="7" cy="4" r="1" fill="#BF5F1F" />
    <circle cx="10" cy="4" r="1" fill="#41784C" />
    <path
      d="M1 6.5H0.5V7V77C0.5 78.3807 1.61929 79.5 3 79.5H111C112.381 79.5 113.5 78.3807 113.5 77V7V6.5H113H1Z"
      fill="#F9FAFB"
    />
    <path
      d="M1 6.5H0.5V7V77C0.5 78.3807 1.61929 79.5 3 79.5H111C112.381 79.5 113.5 78.3807 113.5 77V7V6.5H113H1Z"
      stroke="#E5E7EB"
    />
    <rect x="1" y="7" width="112" height="28" fill={themeColor} />
    <path d="M17 35H97V77C97 78.1046 96.1046 79 95 79H19C17.8954 79 17 78.1046 17 77V35Z" fill="#F9FAFB" />
    <rect x="19" y="21" width="76" height="48" rx="2" fill="white" />
    <rect x="23" y="25" width="16" height="4" rx="1" fill="#F3F4F6" />
    <rect x="23" y="31" width="68" height="4" rx="1" fill="#F3F4F6" />
    <rect x="23" y="37" width="68" height="4" rx="1" fill="#F3F4F6" />
    <rect x="23" y="43" width="15.5" height="4" rx="1" fill="#F3F4F6" />
    <rect x="40.5" y="43" width="15.5" height="4" rx="1" fill="#F3F4F6" />
    <rect x="58" y="43" width="15.5" height="4" rx="1" fill="#F3F4F6" />
    <rect x="75.5" y="43" width="15.5" height="4" rx="1" fill="#F3F4F6" />
    <rect x="23" y="51" width="68" height="4" rx="1" fill="#F3F4F6" />
    <rect x="23" y="59" width="68" height="4" rx="1" fill="#F3F4F6" />
  </svg>
);

export default ThemeSelect;
